import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ThemeToggleButton } from './ThemeToggle';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { name: 'Marketplace', path: '/marketplace' },
    { name: 'Pricing', path: '/pricing' },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'doctor':
        return '/doctor/dashboard';
      case 'patient':
        return '/patient/dashboard';
      case 'clinic_owner':
        return '/clinic/dashboard';
      case 'admin':
        return '/admin/dashboard';
      default:
        return '/';
    }
  };

  return (
    <>
      {/* Skip to main content link - visible only on focus for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-gradient-silver focus:text-obsidian focus:rounded-lg focus:font-mono focus:text-sm focus:uppercase focus:tracking-widest focus:outline-none focus:ring-2 focus:ring-pure-white"
      >
        Skip to main content
      </a>
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-light-border dark:border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <span className="font-display text-2xl italic tracking-tight text-light-text dark:text-pure-white transition-smooth group-hover:text-light-text-secondary dark:group-hover:text-silver">
              Calendiary
            </span>
          </Link>

          {/* Main Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-mono text-xs uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white ${
                  isActive(link.path)
                    ? 'text-light-text dark:text-pure-white'
                    : 'text-light-text-secondary dark:text-silver/60'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {isAuthenticated && (
              <Link
                to={getDashboardLink()}
                className={`font-mono text-xs uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white ${
                  location.pathname.includes('/dashboard')
                    ? 'text-light-text dark:text-pure-white'
                    : 'text-light-text-secondary dark:text-silver/60'
                }`}
              >
                Dashboard
              </Link>
            )}
            {isAuthenticated && user?.role === 'patient' && (
              <>
                <Link
                  to="/patient/appointments"
                  className={`font-mono text-xs uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white ${
                    location.pathname === '/patient/appointments' || location.pathname.startsWith('/patient/appointments/')
                      ? 'text-light-text dark:text-pure-white'
                      : 'text-light-text-secondary dark:text-silver/60'
                  }`}
                >
                  My Bookings
                </Link>
                <Link
                  to="/patient/favorites"
                  className={`font-mono text-xs uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white ${
                    location.pathname === '/patient/favorites'
                      ? 'text-light-text dark:text-pure-white'
                      : 'text-light-text-secondary dark:text-silver/60'
                  }`}
                >
                  Favorites
                </Link>
              </>
            )}
            {isAuthenticated && user?.role === 'admin' && (
              <>
                <Link
                  to="/admin/doctors"
                  className={`font-mono text-xs uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white ${
                    location.pathname === '/admin/doctors'
                      ? 'text-light-text dark:text-pure-white'
                      : 'text-light-text-secondary dark:text-silver/60'
                  }`}
                >
                  Doctors
                </Link>
                <Link
                  to="/admin/reviews"
                  className={`font-mono text-xs uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white ${
                    location.pathname === '/admin/reviews'
                      ? 'text-light-text dark:text-pure-white'
                      : 'text-light-text-secondary dark:text-silver/60'
                  }`}
                >
                  Reviews
                </Link>
              </>
            )}
          </div>

          {/* Auth Section - Desktop */}
          <div className="hidden md:flex items-center gap-4">
            <ThemeToggleButton />
            {isAuthenticated ? (
              <>
                <span
                  className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 max-w-[200px] truncate"
                  title={`${user?.firstName} ${user?.lastName}`}
                >
                  {user?.firstName} {user?.lastName}
                </span>
                <button
                  onClick={handleLogout}
                  className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="btn-primary"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Theme Toggle - Mobile */}
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggleButton />

            {/* Hamburger Menu Button - Mobile */}
            <button
              onClick={toggleMobileMenu}
              className="flex flex-col justify-center items-center w-10 h-10 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-smooth"
              aria-label="Toggle navigation menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span
                className={`block w-6 h-0.5 bg-light-text dark:bg-silver transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-light-text dark:bg-silver transition-all duration-300 my-1 ${
                  isMobileMenuOpen ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-light-text dark:bg-silver transition-all duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
                }`}
              />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="py-4 border-t border-light-border dark:border-border mt-4">
            <div className="flex flex-col gap-4">
              {/* Navigation Links */}
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={closeMobileMenu}
                  className={`font-mono text-sm uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white py-2 ${
                    isActive(link.path)
                      ? 'text-light-text dark:text-pure-white'
                      : 'text-light-text-secondary dark:text-silver/60'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              {isAuthenticated && (
                <Link
                  to={getDashboardLink()}
                  onClick={closeMobileMenu}
                  className={`font-mono text-sm uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white py-2 ${
                    location.pathname.includes('/dashboard')
                      ? 'text-light-text dark:text-pure-white'
                      : 'text-light-text-secondary dark:text-silver/60'
                  }`}
                >
                  Dashboard
                </Link>
              )}
              {isAuthenticated && user?.role === 'patient' && (
                <>
                  <Link
                    to="/patient/appointments"
                    onClick={closeMobileMenu}
                    className={`font-mono text-sm uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white py-2 ${
                      location.pathname === '/patient/appointments' || location.pathname.startsWith('/patient/appointments/')
                        ? 'text-light-text dark:text-pure-white'
                        : 'text-light-text-secondary dark:text-silver/60'
                    }`}
                  >
                    My Bookings
                  </Link>
                  <Link
                    to="/patient/favorites"
                    onClick={closeMobileMenu}
                    className={`font-mono text-sm uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white py-2 ${
                      location.pathname === '/patient/favorites'
                        ? 'text-light-text dark:text-pure-white'
                        : 'text-light-text-secondary dark:text-silver/60'
                    }`}
                  >
                    Favorites
                  </Link>
                </>
              )}
              {isAuthenticated && user?.role === 'admin' && (
                <>
                  <Link
                    to="/admin/doctors"
                    onClick={closeMobileMenu}
                    className={`font-mono text-sm uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white py-2 ${
                      location.pathname === '/admin/doctors'
                        ? 'text-light-text dark:text-pure-white'
                        : 'text-light-text-secondary dark:text-silver/60'
                    }`}
                  >
                    Doctors
                  </Link>
                  <Link
                    to="/admin/reviews"
                    onClick={closeMobileMenu}
                    className={`font-mono text-sm uppercase tracking-widest transition-smooth hover:text-light-text dark:hover:text-pure-white py-2 ${
                      location.pathname === '/admin/reviews'
                        ? 'text-light-text dark:text-pure-white'
                        : 'text-light-text-secondary dark:text-silver/60'
                    }`}
                  >
                    Reviews
                  </Link>
                </>
              )}

              {/* Auth Links */}
              <div className="border-t border-light-border dark:border-border pt-4 mt-2">
                {isAuthenticated ? (
                  <>
                    <span
                      className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-3"
                      title={`${user?.firstName} ${user?.lastName}`}
                    >
                      {user?.firstName} {user?.lastName}
                    </span>
                    <button
                      onClick={() => {
                        handleLogout();
                        closeMobileMenu();
                      }}
                      className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth py-2"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-3">
                    <Link
                      to="/login"
                      onClick={closeMobileMenu}
                      className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white transition-smooth py-2"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={closeMobileMenu}
                      className="btn-primary text-center"
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
    </>
  );
};

export default Navbar;
